'use client'

import { Checkmark, Close, Edit, Unlink } from '@carbon/icons-react'
import { useMediaQuery } from '@mantine/hooks'
import { type Prisma } from '@prisma/client'
import { Button } from 'app/_components/Button'
import AddStoryCard from 'app/(main)/_components/StoryTab/AddStoryCard'
import AddStoryDialog from 'app/(main)/_components/StoryTab/AddStoryDialog'
import StoryTile from 'app/(main)/_components/StoryTab/StoryTile'
import { toByteArray } from 'base64-js'
import type { StoryData } from 'lib/data/story'
import type { Encoded } from 'lib/encoding'
import { notifyUser } from 'lib/form'
import { yDocToSlateElement } from 'lib/yjs-util'
import Image from 'next/image'
import WelcomeIllustration from 'public/newbee-illustration.svg'
import postItImage from 'public/post-it.svg'
import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { toggleSidebar } from '../../actions'
import WidgetContainer from '../WidgetContainer'
import { removeStoryFromChannel } from './actions'

const storiesAtOnce = 3
export default function StoryWidgetClientPart({
  employee,
  stories,
  isNewEmployee,
  channel,
}: {
  employee: Prisma.EmployeeGetPayload<{
    select: {
      entryDate: true
      hideStorySidebar: true
      firstName: true
      id: true
    }
  }>
  stories: Encoded<StoryData>[]
  isNewEmployee: boolean
  channel: { id: string; title: string }
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [unlinkedStoryIds, setUnlinkedStoryIds] = useState<string[]>([])
  const hideStorySidebar = employee.hideStorySidebar
  const filteredStories = stories.filter(
    (story) => !unlinkedStoryIds.includes(story.id),
  )
  const visibleStories = filteredStories.slice(
    0,
    showAll ? stories.length : storiesAtOnce,
  )
  const isMobile = useMediaQuery('(max-width: 1280px)')
  const additionalCount = filteredStories.length - storiesAtOnce
  useEffect(() => {
    setShowAll(isMobile)
  }, [isMobile])

  const handleUnlink = async (storyId: string) => {
    setUnlinkedStoryIds((prev) => [...prev, storyId])
  }

  const handleConfirmation = async () => {
    setIsEditing(false)
    const result = await Promise.all(
      unlinkedStoryIds.map((unlinkedStoryId) =>
        removeStoryFromChannel(channel.id, unlinkedStoryId),
      ),
    )
    const resultWithError = result.find((r) => r.status === 'error')
    if (resultWithError) {
      return notifyUser(resultWithError)
    } else {
      setUnlinkedStoryIds([])
      if (result[0]) {
        notifyUser(result[0])
      }
    }
  }

  return (
    <WidgetContainer
      additionalActions={
        isNewEmployee ? null : (
          <>
            {isEditing ? (
              <>
                <Button
                  aria-label="Änderungen speichern"
                  title="Änderungen speichern"
                  shape="icon"
                  type="button"
                  variant="cta"
                  onClick={handleConfirmation}
                >
                  <Checkmark aria-hidden="true" />
                </Button>
                <Button
                  aria-label="Änderungen verwerfen"
                  title="Änderungen verwerfen"
                  shape="icon"
                  variant="ghost"
                  type="button"
                  className="text-icon-primary"
                  onClick={() => {
                    setIsEditing(false)
                    setUnlinkedStoryIds([])
                  }}
                >
                  <Close aria-hidden="true" />
                </Button>
              </>
            ) : (
              <Button
                aria-label="Beiträge bearbeiten"
                title="Beiträge bearbeiten"
                shape="icon"
                variant="ghost"
                type="button"
                onClick={() => setIsEditing(true)}
              >
                <Edit aria-hidden="true" />
              </Button>
            )}
          </>
        )
      }
      title={
        isNewEmployee ? (
          <>
            <div className="flex flex-col gap-4">
              <Image
                className="mb-2 h-10 w-fit xl:h-12"
                src={WelcomeIllustration}
                width={128}
                height={64}
                alt="Three exclamation marks in a speech bubble"
              />
            </div>
          </>
        ) : (
          <div>
            <span
              className={twMerge(
                'mt-2 block text-xl',
                hideStorySidebar && 'xl:text-xs',
              )}
            >
              Beiträge
            </span>
          </div>
        )
      }
      subtitle={
        isNewEmployee ? (
          <span className="text-2xl">
            Willkommen bei TBF, {employee.firstName}.
          </span>
        ) : (
          <span className="text-text-secondary block text-sm">
            Hier findest du hilfreiche Beiträge zu den Themen, die dich bewegen.
          </span>
        )
      }
      closedText={isNewEmployee ? 'Servus' : 'Beiträge'}
      toggleAction={toggleSidebar.bind(null, {
        hide: !hideStorySidebar,
        id: employee.id,
        type: 'story',
      })}
      collapsed={hideStorySidebar}
      align="right"
    >
      <div className="flex flex-col gap-4 overflow-y-hidden xl:overflow-y-auto">
        {isNewEmployee && (
          <p className="text-text-secondary text-sm">
            Du hast in den letzten Wochen bei uns gestartet. Die folgenden
            Seiten helfen Dir, Dich bei TBF zurechtzufinden.
          </p>
        )}
        <div className="flex gap-4 xl:flex-col">
          {visibleStories.map((story) => (
            <StoryTile
              interactive={
                isEditing && (
                  <Button
                    aria-label={`Verknüpfung zu ${story.title || 'Beitrag'} entfernen`}
                    title={`Verknüpfung zu ${story.title || 'Beitrag'} entfernen`}
                    shape="icon"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      handleUnlink(story.id)
                    }}
                    variant="cta"
                  >
                    <Unlink aria-hidden="true" />
                  </Button>
                )
              }
              key={story.id}
              href={`/stories/${story.id}`}
              updatedAt={story.versions?.[0]?.createdAt}
              createdAt={story.createdAt}
              title={story.title}
              richText={yDocToSlateElement(
                story.doc ? toByteArray(story.doc) : null,
              )}
              className="border-gray-disabled w-full min-w-72 border sm:min-w-96 xl:min-w-fit"
            />
          ))}
          {!isNewEmployee && (
            <AddStoryCard
              image={postItImage}
              text="Wähle einen Beitrag, um ihn für alle anzupinnen"
              className="border-gray-disabled w-full min-w-72 border border-dashed sm:min-w-96 xl:min-w-fit"
              AddStoryDialog={
                <AddStoryDialog
                  channelId={channel.id}
                  className="text-link-primary bg-white px-4 py-2"
                />
              }
            />
          )}
          {Boolean(!showAll && additionalCount > 0) && (
            <div className="-mt-2 mr-0.5 text-right">
              <Button
                variant="ghost"
                className="text-link-primary inline-block text-right"
                onClick={() => setShowAll(!showAll)}
              >
                {additionalCount} weitere
              </Button>
            </div>
          )}
        </div>
      </div>
    </WidgetContainer>
  )
}
